import { useState, useEffect, useRef } from 'react'
import { api } from '../lib/api'
import {
  Users,
  Building2,
  Search,
  ChevronLeft,
  ChevronRight,
  Shield,
  ShieldCheck,
  TrendingUp,
  BarChart3,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Monitor,
  Trash2,
  MoreVertical,
  RefreshCw,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Lock,
  Download
} from 'lucide-react'
import { toast } from 'react-toastify'

type Tab = 'users' | 'brands'

interface QueueJob {
  id: string
  name: string
  status: 'active' | 'waiting'
  brandId?: string
  brandName?: string
  userBrandId?: string
  createdAt: string
  age: number // seconds
}

interface QueueStatus {
  active: number
  waiting: number
  delayed: number
  failed: number
  completed: number
  total: number
  error?: string
  jobs?: QueueJob[]
}

interface QueueManagementState {
  loading: boolean
  results: Record<string, QueueStatus> | null
  lastAction: string | null
}

function QueueManagement() {
  const [state, setState] = useState<QueueManagementState>({
    loading: false,
    results: null,
    lastAction: null
  })
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const handleQueueAction = async (action: 'status' | 'clear-failed' | 'clear-stalled') => {
    setDropdownOpen(false)
    try {
      setState(prev => ({ ...prev, loading: true, lastAction: action }))

      const response = await api.post('/admin/queue-management', { action })

      if (response.data.success) {
        setState({
          loading: false,
          results: response.data.results,
          lastAction: action
        })

        if (action === 'clear-failed' || action === 'clear-stalled') {
          const totalCleared = Object.values(response.data.results).reduce((sum: number, result: any) => {
            return sum + (result.cleared || 0)
          }, 0)
          toast.success(`${action === 'clear-failed' ? 'Failed' : 'Stalled'} jobs cleared: ${totalCleared}`)
        }
      }
    } catch (error: any) {
      console.error('Queue management error:', error)
      toast.error(error.response?.data?.error || 'Failed to perform queue action')
      setState(prev => ({ ...prev, loading: false }))
    }
  }

  const getStatusColor = (status: QueueStatus) => {
    if (status.error) return 'bg-red-50 border-red-200'
    if (status.failed > 0 || status.active > 10) return 'bg-yellow-50 border-yellow-200'
    return 'bg-green-50 border-green-200'
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Monitor className="w-6 h-6 text-gray-600" />
              Queue Management
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Monitor and manage background job queues
            </p>
          </div>

          {/* Action Buttons - Desktop (hidden on mobile) */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => handleQueueAction('status')}
              disabled={state.loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {state.loading && state.lastAction === 'status' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4" />
              )}
              Check Status
            </button>

            <button
              onClick={() => handleQueueAction('clear-failed')}
              disabled={state.loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {state.loading && state.lastAction === 'clear-failed' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Clear Failed
            </button>

            <button
              onClick={() => handleQueueAction('clear-stalled')}
              disabled={state.loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {state.loading && state.lastAction === 'clear-stalled' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Clear Stalled
            </button>
          </div>

          {/* Action Dropdown - Mobile (shown on mobile only) */}
          <div className="relative md:hidden">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              disabled={state.loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {state.loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MoreVertical className="w-4 h-4" />
              )}
              Actions
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setDropdownOpen(false)}
                />

                {/* Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <button
                    onClick={() => handleQueueAction('status')}
                    disabled={state.loading}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <BarChart3 className="w-4 h-4 text-indigo-600" />
                    Check Status
                  </button>

                  <button
                    onClick={() => handleQueueAction('clear-failed')}
                    disabled={state.loading}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-orange-600" />
                    Clear Failed
                  </button>

                  <button
                    onClick={() => handleQueueAction('clear-stalled')}
                    disabled={state.loading}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 text-red-600" />
                    Clear Stalled
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Results Display */}
        {state.results && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {state.lastAction === 'status' ? 'Queue Status' :
                 state.lastAction === 'clear-failed' ? 'Failed Jobs Cleared' :
                 'Stalled Jobs Cleared'}
              </h3>
              <span className="text-xs text-gray-500">
                {Object.keys(state.results).length} queues
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(state.results).map(([queueName, status]) => (
                <div
                  key={queueName}
                  className={`border-2 rounded-lg p-4 transition-colors ${getStatusColor(status)}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 text-sm">{queueName}</h4>
                    {status.error ? (
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">Error</span>
                    ) : status.failed > 0 || status.active > 10 ? (
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">⚠️</span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">✓</span>
                    )}
                  </div>

                  {status.error ? (
                    <p className="text-xs text-red-600">{status.error}</p>
                  ) : state.lastAction === 'status' ? (
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Active:</span>
                        <span className={`font-medium ${status.active > 0 ? 'text-cyan-600' : 'text-gray-900'}`}>
                          {status.active}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Waiting:</span>
                        <span className={`font-medium ${status.waiting > 0 ? 'text-yellow-600' : 'text-gray-900'}`}>
                          {status.waiting}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Failed:</span>
                        <span className={`font-medium ${status.failed > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {status.failed}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Completed:</span>
                        <span className="font-medium text-green-600">{status.completed}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-gray-200">
                        <span className="text-gray-600">Total:</span>
                        <span className="font-semibold text-gray-900">{status.total}</span>
                      </div>

                      {/* Job details with brand names */}
                      {status.jobs && status.jobs.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-gray-200">
                          <p className="text-gray-500 mb-2 font-medium">Active/Waiting Jobs:</p>
                          <div className="space-y-1.5 max-h-32 overflow-y-auto">
                            {status.jobs.map((job) => (
                              <div
                                key={job.id}
                                className={`flex items-center justify-between py-1 px-2 rounded ${
                                  job.status === 'active' ? 'bg-cyan-50' : 'bg-yellow-50'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                                    job.status === 'active' ? 'bg-cyan-500' : 'bg-yellow-500'
                                  }`} />
                                  <span className="font-medium text-gray-900 truncate">
                                    {job.brandName || job.brandId?.slice(0, 8) || job.name}
                                  </span>
                                </div>
                                <span className="text-gray-500 ml-2 whitespace-nowrap">
                                  {job.age < 60 ? `${job.age}s` : job.age < 3600 ? `${Math.floor(job.age / 60)}m` : `${Math.floor(job.age / 3600)}h`}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm">
                      <span className="text-gray-600">Cleared: </span>
                      <span className="font-semibold text-gray-900">
                        {(status as any).cleared || 0}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!state.results && !state.loading && (
          <div className="text-center py-12">
            <Monitor className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600">
              Click "Check Status" to view queue information
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

interface UserSession {
  id: string
  deviceInfo: string | null
  ipAddress: string | null
  lastActiveAt: string
  expiresAt: string
  createdAt: string
}

interface User {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  isAdmin: boolean
  stripeCustomerId: string | null
  shopifyShopId: string | null
  createdAt: string
  apiCosts30Days?: number
  apiCalls30Days?: number
  userBrands: {
    id: string
    customName: string | null
    isOwner: boolean
    onboardingCompleted: boolean
    createdAt: string
    subscriptionProduct: string | null
    subscriptionStatus: string | null
    trialEndsAt: string | null
    brand: {
      id: string
      companyName: string
      domain: string
      industry: string | null
      aiIndexScore: number | null
    }
  }[]
}

interface Brand {
  id: string
  companyName: string
  domain: string
  industry: string | null
  description: string | null
  aiIndexScore: number | null
  hasLlmsTxt: boolean
  createdAt: string
  updatedAt: string
  totalQueries: number | null
  totalResults: number | null
  cachedMentionRate: number | null
  userBrands: {
    id: string
    user: {
      id: string
      email: string
      firstName: string | null
      lastName: string | null
    }
  }[]
}

interface ProcessingStatus {
  searchAnalysisStarted: boolean
  searchAnalysisComplete: boolean
  searchAnalysisCompletedAt: string | null
  marketAnalysisStarted: boolean
  marketAnalysisComplete: boolean
  marketAnalysisCompletedAt: string | null
  contentEnrichmentStarted: boolean
  contentEnrichmentComplete: boolean
  contentEnrichmentCompletedAt: string | null
  searchInsightsStarted: boolean
  searchInsightsComplete: boolean
  searchInsightsCompletedAt: string | null
  marketInsightsStarted: boolean
  marketInsightsComplete: boolean
  marketInsightsCompletedAt: string | null
  brandInsightsStarted: boolean
  brandInsightsComplete: boolean
  brandInsightsCompletedAt: string | null
}

interface BrandDetails {
  id: string
  companyName: string
  domain: string
  industry: string | null
  description: string | null
  aiIndexScore: number | null
  indexGrade: string | null
  hasLlmsTxt: boolean
  cachedMentionRate: number | null
  createdAt: string
  updatedAt: string
  content: {
    total: number
    enriched: number
    pending: number
    failed: number
  }
  totalQueries: number
  competitors: {
    total: number
    headToHead: number
  }
  userBrands: {
    id: string
    customName: string | null
    isOwner: boolean
    onboardingCompleted: boolean
    createdAt: string
    subscriptionProduct: string | null
    subscriptionStatus: string | null
    trialEndsAt: string | null
    cachedMentionRate: number | null // User-specific mention rate
    user: {
      id: string
      email: string
      firstName: string | null
      lastName: string | null
    }
    processingStatus: ProcessingStatus | null
    insights: {
      searchInsights: { completedAt: string } | null
      marketInsights: { completedAt: string } | null
      brandInsights: { completedAt: string } | null
      marketReport: { completedAt: string; expiresAt: string } | null
    }
  }[]
}

interface Stats {
  totalUsers: number
  totalBrands: number
  paidUsers: number
  trialUsers: number
  totalQueries: number
}

interface Pagination {
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function Admin() {
  const [activeTab, setActiveTab] = useState<Tab>('users')
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<Stats | null>(null)

  // Users state
  const [users, setUsers] = useState<User[]>([])
  const [usersSearch, setUsersSearch] = useState('')
  const [usersTypeFilter, setUsersTypeFilter] = useState<string>('')
  const [usersPlanFilter, setUsersPlanFilter] = useState<string>('')
  const [usersPagination, setUsersPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  })
  const [apiCostSummary, setApiCostSummary] = useState<{
    last30Days: { totalCost: number; totalCalls: number }
    today: { totalCost: number; totalCalls: number }
  } | null>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [userSessions, setUserSessions] = useState<UserSession[]>([])
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [impersonating, setImpersonating] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Brands state
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandsSearch, setBrandsSearch] = useState('')
  const [brandsSortBy, setBrandsSortBy] = useState<'companyName' | 'domain' | 'industry' | 'aiIndexScore' | 'totalQueries' | 'userCount'>('userCount')
  const [brandsSortOrder, setBrandsSortOrder] = useState<'asc' | 'desc'>('desc')
  const [brandsPagination, setBrandsPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  })
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false)
  const [brandDetails, setBrandDetails] = useState<BrandDetails | null>(null)
  const [loadingBrandDetails, setLoadingBrandDetails] = useState(false)
  const [exportingContext, setExportingContext] = useState(false)

  // Download brand context as markdown
  const handleExportBrandContext = async (brandId: string, companyName: string) => {
    try {
      setExportingContext(true)
      const response = await api.get(`/admin/brands/${brandId}/export-context`, {
        responseType: 'blob'
      })

      // Create download link
      const blob = new Blob([response.data], { type: 'text/markdown' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const safeCompanyName = companyName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      link.download = `${safeCompanyName}-brand-context.md`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Brand context exported successfully')
    } catch (error) {
      console.error('Failed to export brand context:', error)
      toast.error('Failed to export brand context')
    } finally {
      setExportingContext(false)
    }
  }

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/admin/stats')
        setStats(response.data.data)
      } catch (error) {
        console.error('Failed to fetch admin stats:', error)
        toast.error('Failed to load admin statistics')
      }
    }
    fetchStats()
  }, [])

  // Fetch users when tab changes or search/page/filters change
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers()
    }
  }, [activeTab, usersSearch, usersPagination.page, usersTypeFilter, usersPlanFilter])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch brands when tab changes or search/page/sort changes
  useEffect(() => {
    if (activeTab === 'brands') {
      fetchBrands()
    }
  }, [activeTab, brandsSearch, brandsPagination.page, brandsSortBy, brandsSortOrder])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/users', {
        params: {
          search: usersSearch || undefined,
          page: usersPagination.page,
          limit: usersPagination.limit,
          type: usersTypeFilter || undefined,
          plan: usersPlanFilter || undefined
        }
      })
      setUsers(response.data.data.users)
      setUsersPagination(response.data.data.pagination)
      if (response.data.data.apiCostSummary) {
        setApiCostSummary(response.data.data.apiCostSummary)
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const fetchBrands = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/brands', {
        params: {
          search: brandsSearch || undefined,
          page: brandsPagination.page,
          limit: brandsPagination.limit,
          sortBy: brandsSortBy,
          sortOrder: brandsSortOrder
        }
      })
      setBrands(response.data.data.brands)
      setBrandsPagination(response.data.data.pagination)
    } catch (error) {
      console.error('Failed to fetch brands:', error)
      toast.error('Failed to load brands')
    } finally {
      setLoading(false)
    }
  }

  const handleImpersonate = async (userId: string, userEmail: string) => {
    try {
      setImpersonating(true)
      const response = await api.post(`/admin/impersonate/${userId}`, {
        reason: 'Admin support session'
      })

      if (response.data.success) {
        toast.success(`Now impersonating ${userEmail}`)

        // The server sets the session cookie automatically
        // Close modal and redirect to dashboard
        handleCloseUserModal()
        window.location.href = '/dashboard'
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to start impersonation'
      toast.error(message)
      console.error('Failed to impersonate user:', error)
    } finally {
      setImpersonating(false)
    }
  }

  const fetchUserSessions = async (userId: string) => {
    try {
      setLoadingSessions(true)
      const response = await api.get(`/admin/users/${userId}/sessions`)
      setUserSessions(response.data.data)
    } catch (error) {
      console.error('Failed to fetch user sessions:', error)
      toast.error('Failed to load user sessions')
    } finally {
      setLoadingSessions(false)
    }
  }

  const handleDeleteSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to delete this session? The user will be logged out.')) {
      return
    }

    try {
      await api.delete(`/admin/sessions/${sessionId}`)
      toast.success('Session deleted successfully')

      // Refresh sessions list
      if (selectedUser) {
        await fetchUserSessions(selectedUser.id)
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to delete session'
      toast.error(message)
      console.error('Failed to delete session:', error)
    }
  }

  const handleUsersSearch = (value: string) => {
    setUsersSearch(value)
    setUsersPagination({ ...usersPagination, page: 1 }) // Reset to page 1 on search
  }

  const handleUsersTypeFilter = (value: string) => {
    setUsersTypeFilter(value)
    setUsersPagination({ ...usersPagination, page: 1 })
  }

  const handleUsersPlanFilter = (value: string) => {
    setUsersPlanFilter(value)
    setUsersPagination({ ...usersPagination, page: 1 })
  }

  // Helper to get user type (STRIPE or SHOPIFY)
  const getUserType = (user: User): 'SHOPIFY' | 'STRIPE' | null => {
    if (user.shopifyShopId) return 'SHOPIFY'
    if (user.stripeCustomerId) return 'STRIPE'
    return null
  }

  // Helper to get highest plan across user's brands
  const getUserPlan = (user: User): string | null => {
    const plans = user.userBrands
      .map(ub => ub.subscriptionProduct)
      .filter(Boolean) as string[]

    if (plans.length === 0) return null

    // Priority: PRO > GROWTH > STARTER > FREE
    const priority = ['PRO', 'GROWTH', 'STARTER', 'FREE']
    for (const p of priority) {
      if (plans.includes(p)) return p
    }
    return plans[0]
  }

  const handleBrandsSearch = (value: string) => {
    setBrandsSearch(value)
    setBrandsPagination({ ...brandsPagination, page: 1 }) // Reset to page 1 on search
  }

  const handleBrandsSort = (field: typeof brandsSortBy) => {
    if (brandsSortBy === field) {
      // Toggle sort order if clicking same field
      setBrandsSortOrder(brandsSortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field and default to desc
      setBrandsSortBy(field)
      setBrandsSortOrder('desc')
    }
    setBrandsPagination({ ...brandsPagination, page: 1 }) // Reset to page 1 on sort change
  }

  const getSortIcon = (field: typeof brandsSortBy) => {
    if (brandsSortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />
    }
    return brandsSortOrder === 'asc'
      ? <ArrowUp className="w-4 h-4 text-indigo-600" />
      : <ArrowDown className="w-4 h-4 text-indigo-600" />
  }

  const handleUserClick = async (user: User) => {
    setSelectedUser(user)
    setIsUserModalOpen(true)
    await fetchUserSessions(user.id)
  }

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false)
    setSelectedUser(null)
    setUserSessions([])
  }

  const handleBrandClick = async (brand: Brand) => {
    setSelectedBrand(brand)
    setIsBrandModalOpen(true)
    setBrandDetails(null)
    setLoadingBrandDetails(true)

    try {
      const response = await api.get(`/admin/brands/${brand.id}/details`)
      if (response.data.success) {
        setBrandDetails(response.data.data)
      }
    } catch (error) {
      console.error('Failed to fetch brand details:', error)
      toast.error('Failed to load brand details')
    } finally {
      setLoadingBrandDetails(false)
    }
  }

  const handleCloseBrandModal = () => {
    setIsBrandModalOpen(false)
    setSelectedBrand(null)
    setBrandDetails(null)
  }

  const handleRegenerate = async (
    userBrandId: string,
    type: string,
    brandName: string,
    options?: { status?: string; contentType?: string }
  ) => {
    setOpenDropdown(null)

    const typeLabels: { [key: string]: string } = {
      'all': 'All Analysis',
      'scores': 'Brand Index Score',
      'insights': 'Brand Insights',
      'search-insights': 'Search Intelligence',
      'market-insights': 'Market Insights',
      'serp-citations': 'SERP & Citation Ranking',
      'resync-products': 'Re-sync Products',
      'enrich-content': 'Enrich Content',
      'enrich-failed': 'Enrich Failed Content',
      'enrich-pending': 'Enrich Pending Content',
      'enrich-products': 'Enrich Products',
      'enrich-articles': 'Enrich Articles',
      'generate-questions': 'Generate Brand Questions',
      'llms': 'LLMS.txt'
    }

    try {
      await api.post(`/admin/user-brands/${userBrandId}/regenerate`, { type, ...options })
      toast.success(`${typeLabels[type] || type} started for ${brandName}`)
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to start regeneration'
      toast.error(message)
      console.error('Failed to start regeneration:', error)
    }
  }

  return (
    <div className="min-h-screen space-y-6">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-indigo-600" />
            Admin Panel
          </h1>
          <p className="text-gray-600">Manage users and brands</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Brands</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalBrands}</p>
                </div>
                <Building2 className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Paid Users</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.paidUsers}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-indigo-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Trial Users</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.trialUsers}</p>
                </div>
                <ShieldCheck className="w-8 h-8 text-purple-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Queries</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalQueries}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-orange-500" />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('users')}
                className={`
                  flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm
                  ${activeTab === 'users'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Users className="w-5 h-5" />
                Users
              </button>
              <button
                onClick={() => setActiveTab('brands')}
                className={`
                  flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm
                  ${activeTab === 'brands'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Building2 className="w-5 h-5" />
                Brands
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                {/* API Cost Summary */}
                {apiCostSummary && (
                  <div className="mb-6 grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                      <div className="text-sm font-medium text-blue-700 mb-1">Last 30 Days (All Users)</div>
                      <div className="flex items-baseline space-x-2">
                        <span className="text-2xl font-bold text-blue-900">${apiCostSummary.last30Days.totalCost.toFixed(2)}</span>
                        <span className="text-sm text-blue-600">{apiCostSummary.last30Days.totalCalls.toLocaleString()} calls</span>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                      <div className="text-sm font-medium text-green-700 mb-1">Today</div>
                      <div className="flex items-baseline space-x-2">
                        <span className="text-2xl font-bold text-green-900">${apiCostSummary.today.totalCost.toFixed(2)}</span>
                        <span className="text-sm text-green-600">{apiCostSummary.today.totalCalls.toLocaleString()} calls</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Search and Filters */}
                <div className="mb-6 space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by email, first name, or last name..."
                      value={usersSearch}
                      onChange={(e) => handleUsersSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {/* Filters */}
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Type:</label>
                      <select
                        value={usersTypeFilter}
                        onChange={(e) => handleUsersTypeFilter(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">All</option>
                        <option value="STRIPE">Stripe</option>
                        <option value="SHOPIFY">Shopify</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Plan:</label>
                      <select
                        value={usersPlanFilter}
                        onChange={(e) => handleUsersPlanFilter(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">All</option>
                        <option value="FREE">Free</option>
                        <option value="STARTER">Starter</option>
                        <option value="GROWTH">Growth</option>
                        <option value="PRO">Pro</option>
                      </select>
                    </div>

                    {(usersTypeFilter || usersPlanFilter) && (
                      <button
                        onClick={() => {
                          setUsersTypeFilter('')
                          setUsersPlanFilter('')
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>

                {/* Users Table */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No users found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date Joined
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Brands
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Plan
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            30-Day API Cost
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => {
                          const userType = getUserType(user)
                          const userPlan = getUserPlan(user)

                          return (
                            <tr
                              key={user.id}
                              onClick={() => handleUserClick(user)}
                              className="hover:bg-gray-50 cursor-pointer"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">
                                      {user.firstName || user.lastName
                                        ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                                        : 'No Name'}
                                    </span>
                                    {user.isAdmin && (
                                      <Shield className="w-4 h-4 text-purple-600" />
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">{user.email}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">
                                  {new Date(user.createdAt).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-indigo-600">
                                  {user.userBrands.length}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm">
                                  {userType === 'SHOPIFY' ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                      Shopify
                                    </span>
                                  ) : userType === 'STRIPE' ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                      Stripe
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm">
                                  {userPlan ? (
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                      userPlan === 'PRO'
                                        ? 'bg-purple-100 text-purple-700'
                                        : userPlan === 'GROWTH'
                                        ? 'bg-indigo-100 text-indigo-700'
                                        : userPlan === 'STARTER'
                                        ? 'bg-cyan-100 text-cyan-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {userPlan}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm">
                                  {user.apiCosts30Days !== undefined ? (
                                    <div className="flex flex-col">
                                      <span className="font-medium text-gray-900">
                                        ${user.apiCosts30Days.toFixed(2)}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {user.apiCalls30Days} calls
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {usersPagination.totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Showing {((usersPagination.page - 1) * usersPagination.limit) + 1} to{' '}
                      {Math.min(usersPagination.page * usersPagination.limit, usersPagination.total)} of{' '}
                      {usersPagination.total} users
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setUsersPagination({ ...usersPagination, page: usersPagination.page - 1 })}
                        disabled={usersPagination.page === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setUsersPagination({ ...usersPagination, page: usersPagination.page + 1 })}
                        disabled={usersPagination.page === usersPagination.totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Brands Tab */}
            {activeTab === 'brands' && (
              <div>
                {/* Search */}
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by company name, domain, or industry..."
                      value={brandsSearch}
                      onChange={(e) => handleBrandsSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* Brands Table */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  </div>
                ) : brands.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No brands found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleBrandsSort('companyName')}
                          >
                            <div className="flex items-center gap-2">
                              <span>Brand Name</span>
                              {getSortIcon('companyName')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleBrandsSort('domain')}
                          >
                            <div className="flex items-center gap-2">
                              <span>Domain</span>
                              {getSortIcon('domain')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleBrandsSort('industry')}
                          >
                            <div className="flex items-center gap-2">
                              <span>Industry</span>
                              {getSortIcon('industry')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleBrandsSort('aiIndexScore')}
                          >
                            <div className="flex items-center gap-2">
                              <span>Index Score</span>
                              {getSortIcon('aiIndexScore')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleBrandsSort('totalQueries')}
                          >
                            <div className="flex items-center gap-2">
                              <span>Queries</span>
                              {getSortIcon('totalQueries')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleBrandsSort('userCount')}
                          >
                            <div className="flex items-center gap-2">
                              <span>Users</span>
                              {getSortIcon('userCount')}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {brands.map((brand) => (
                          <tr
                            key={brand.id}
                            onClick={() => handleBrandClick(brand)}
                            className="hover:bg-gray-50 cursor-pointer"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{brand.companyName}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{brand.domain}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{brand.industry || '-'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-indigo-600">
                                {brand.aiIndexScore !== null ? brand.aiIndexScore : '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">
                                {brand.totalQueries !== null ? brand.totalQueries : '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{brand.userBrands.length}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {brandsPagination.totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Showing {((brandsPagination.page - 1) * brandsPagination.limit) + 1} to{' '}
                      {Math.min(brandsPagination.page * brandsPagination.limit, brandsPagination.total)} of{' '}
                      {brandsPagination.total} brands
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setBrandsPagination({ ...brandsPagination, page: brandsPagination.page - 1 })}
                        disabled={brandsPagination.page === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setBrandsPagination({ ...brandsPagination, page: brandsPagination.page + 1 })}
                        disabled={brandsPagination.page === brandsPagination.totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Queue Management Section */}
      <QueueManagement />

      {/* User Details Modal */}
      {isUserModalOpen && selectedUser && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
          onClick={handleCloseUserModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  {selectedUser.firstName || selectedUser.lastName
                    ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim()
                    : 'No Name'}
                  {selectedUser.isAdmin && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                </h2>
                <p className="text-sm text-gray-600 mt-1">{selectedUser.email}</p>
              </div>
              <button
                onClick={handleCloseUserModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Profile Overview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Profile Overview</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Date Joined</label>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {new Date(selectedUser.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Total Brands</label>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {selectedUser.userBrands.length}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Stripe Customer ID</label>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {selectedUser.stripeCustomerId ? (
                        <span className="text-xs text-gray-600">{selectedUser.stripeCustomerId}</span>
                      ) : (
                        <span className="text-gray-400">Not connected</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Associated Brands */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Associated Brands ({selectedUser.userBrands.length})
                </h3>
                {selectedUser.userBrands.length === 0 ? (
                  <p className="text-sm text-gray-500">No brands associated with this user</p>
                ) : (
                  <div className="space-y-3">
                    {selectedUser.userBrands.map((ub) => (
                      <div
                        key={ub.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-gray-900">{ub.brand.companyName}</h4>
                              {ub.isOwner && (
                                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  Owner
                                </span>
                              )}
                              {ub.subscriptionProduct && (
                                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                  {ub.subscriptionProduct}
                                </span>
                              )}
                              {ub.subscriptionStatus && (
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                  ub.subscriptionStatus === 'ACTIVE'
                                    ? 'bg-green-100 text-green-700'
                                    : ub.subscriptionStatus === 'TRIAL'
                                    ? 'bg-blue-100 text-blue-700'
                                    : ub.subscriptionStatus === 'PAST_DUE'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {ub.subscriptionStatus}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{ub.brand.domain}</p>
                            <div className="flex items-center gap-4 text-sm flex-wrap">
                              {ub.brand.industry && (
                                <div>
                                  <span className="text-gray-500">Industry:</span>
                                  <span className="ml-1 font-medium text-gray-700">{ub.brand.industry}</span>
                                </div>
                              )}
                              {ub.brand.aiIndexScore !== null && (
                                <div>
                                  <span className="text-gray-500">AI Score:</span>
                                  <span className="ml-1 font-medium text-indigo-600">{ub.brand.aiIndexScore}</span>
                                </div>
                              )}
                              {ub.trialEndsAt && ub.subscriptionStatus === 'TRIAL' && (
                                <div>
                                  <span className="text-gray-500">Trial Ends:</span>
                                  <span className="ml-1 font-medium text-gray-700">
                                    {new Date(ub.trialEndsAt).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Regeneration Actions Dropdown */}
                          <div className="relative" ref={openDropdown === ub.id ? dropdownRef : null}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenDropdown(openDropdown === ub.id ? null : ub.id)
                              }}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Regeneration actions"
                            >
                              <MoreVertical className="w-5 h-5 text-gray-500" />
                            </button>

                            {/* Dropdown Menu */}
                            {openDropdown === ub.id && (
                              <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                <div className="py-1">
                                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                    Regenerate
                                  </div>

                                  <button
                                    onClick={() => handleRegenerate(ub.id, 'all', ub.brand.companyName)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    All Analysis
                                  </button>

                                  <button
                                    onClick={() => handleRegenerate(ub.id, 'scores', ub.brand.companyName)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    Brand Index Score
                                  </button>

                                  <button
                                    onClick={() => handleRegenerate(ub.id, 'insights', ub.brand.companyName)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    Brand Insights
                                  </button>

                                  <button
                                    onClick={() => handleRegenerate(ub.id, 'search-insights', ub.brand.companyName)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    Search Intelligence
                                  </button>

                                  <button
                                    onClick={() => handleRegenerate(ub.id, 'market-insights', ub.brand.companyName)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    Market Insights
                                  </button>

                                  <button
                                    onClick={() => handleRegenerate(ub.id, 'serp-citations', ub.brand.companyName)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    SERP & Citation Ranking
                                  </button>

                                  <button
                                    onClick={() => handleRegenerate(ub.id, 'generate-questions', ub.brand.companyName)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    Generate Brand Questions
                                  </button>

                                  <button
                                    onClick={() => handleRegenerate(ub.id, 'llms', ub.brand.companyName)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    Regenerate LLMS.txt
                                  </button>

                                  {/* Re-sync Products - Only for Shopify brands */}
                                  {selectedUser?.shopifyShopId && (
                                    <button
                                      onClick={() => handleRegenerate(ub.id, 'resync-products', ub.brand.companyName)}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors border-t border-gray-100"
                                    >
                                      <RefreshCw className="w-4 h-4" />
                                      Re-sync Products
                                    </button>
                                  )}

                                  {/* Enrich Content - Only for Shopify brands */}
                                  {selectedUser?.shopifyShopId && (
                                    <>
                                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t border-gray-100 mt-1">
                                        Enrich Content
                                      </div>
                                      <button
                                        onClick={() => handleRegenerate(ub.id, 'enrich-failed', ub.brand.companyName, { status: 'failed' })}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 flex items-center gap-2 transition-colors"
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                        Failed Only
                                      </button>
                                      <button
                                        onClick={() => handleRegenerate(ub.id, 'enrich-pending', ub.brand.companyName, { status: 'pending' })}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 flex items-center gap-2 transition-colors"
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                        Pending Only
                                      </button>
                                      <button
                                        onClick={() => handleRegenerate(ub.id, 'enrich-content', ub.brand.companyName, { status: 'both' })}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                        All Unenriched
                                      </button>
                                      <div className="border-t border-gray-100 my-1" />
                                      <button
                                        onClick={() => handleRegenerate(ub.id, 'enrich-products', ub.brand.companyName, { status: 'both', contentType: 'PRODUCT' })}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                        Products Only
                                      </button>
                                      <button
                                        onClick={() => handleRegenerate(ub.id, 'enrich-articles', ub.brand.companyName, { status: 'both', contentType: 'ARTICLE' })}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2 transition-colors"
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                        Articles Only
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Active Sessions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-indigo-600" />
                  Active Sessions ({userSessions.length})
                </h3>
                {loadingSessions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                  </div>
                ) : userSessions.length === 0 ? (
                  <p className="text-sm text-gray-500">No active sessions</p>
                ) : (
                  <div className="space-y-3">
                    {userSessions.map((session) => (
                      <div
                        key={session.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-red-300 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Monitor className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-900">
                                {session.deviceInfo || 'Unknown Device'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              {session.ipAddress && (
                                <div>
                                  <span className="text-gray-500">IP Address:</span>
                                  <span className="ml-1 font-medium text-gray-700">{session.ipAddress}</span>
                                </div>
                              )}
                              <div>
                                <span className="text-gray-500">Last Active:</span>
                                <span className="ml-1 font-medium text-gray-700">
                                  {new Date(session.lastActiveAt).toLocaleString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Created:</span>
                                <span className="ml-1 font-medium text-gray-700">
                                  {new Date(session.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Expires:</span>
                                <span className="ml-1 font-medium text-gray-700">
                                  {new Date(session.expiresAt).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteSession(session.id)}
                            className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete session (force logout)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => handleImpersonate(selectedUser.id, selectedUser.email)}
                disabled={impersonating}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {impersonating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                {impersonating ? 'Starting...' : 'Impersonate User'}
              </button>
              <button
                onClick={handleCloseUserModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand Details Modal */}
      {isBrandModalOpen && selectedBrand && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
          onClick={handleCloseBrandModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  {selectedBrand.companyName}
                  {selectedBrand.hasLlmsTxt && (
                    <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      LLMS.txt
                    </span>
                  )}
                </h2>
                <p className="text-sm text-gray-600 mt-1">{selectedBrand.domain}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleExportBrandContext(selectedBrand.id, selectedBrand.companyName)}
                  disabled={exportingContext}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Export brand context as markdown for Claude prompting"
                >
                  {exportingContext ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Export .MD
                </button>
                <button
                  onClick={handleCloseBrandModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              {loadingBrandDetails ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  <span className="ml-2 text-gray-600">Loading brand details...</span>
                </div>
              ) : brandDetails ? (
                <>
                  {/* Brand Overview */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Brand Overview</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wider">Industry</label>
                        <p className="mt-1 text-sm font-medium text-gray-900">{brandDetails.industry || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wider">Created</label>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {new Date(brandDetails.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wider">Last Updated</label>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {new Date(brandDetails.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wider">Users</label>
                        <p className="mt-1 text-sm font-medium text-gray-900">{brandDetails.userBrands.length}</p>
                      </div>
                    </div>
                    {brandDetails.description && (
                      <div className="mt-4">
                        <label className="text-xs text-gray-500 uppercase tracking-wider">Description</label>
                        <p className="mt-1 text-sm text-gray-700">{brandDetails.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Key Metrics */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Metrics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {/* Index Grade */}
                      <div className="bg-indigo-50 rounded-lg p-3">
                        <label className="text-xs text-indigo-600 uppercase tracking-wider font-medium">Index Grade</label>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className={`text-2xl font-bold ${
                            brandDetails.indexGrade === 'A' ? 'text-green-600' :
                            brandDetails.indexGrade === 'B' ? 'text-blue-600' :
                            brandDetails.indexGrade === 'C' ? 'text-yellow-600' :
                            brandDetails.indexGrade === 'D' ? 'text-orange-600' :
                            brandDetails.indexGrade === 'F' ? 'text-red-600' : 'text-gray-400'
                          }`}>
                            {brandDetails.indexGrade || '-'}
                          </span>
                          <span className="text-sm text-indigo-600">
                            ({brandDetails.aiIndexScore !== null ? brandDetails.aiIndexScore : '-'})
                          </span>
                        </div>
                      </div>

                      {/* Content Consumed */}
                      <div className="bg-blue-50 rounded-lg p-3">
                        <label className="text-xs text-blue-600 uppercase tracking-wider font-medium">Content</label>
                        <p className="mt-1 text-2xl font-bold text-blue-900">{brandDetails.content.total}</p>
                        <p className="text-xs text-blue-600">consumed</p>
                      </div>

                      {/* Enriched Content */}
                      <div className="bg-green-50 rounded-lg p-3">
                        <label className="text-xs text-green-600 uppercase tracking-wider font-medium">Enriched</label>
                        <p className="mt-1 text-2xl font-bold text-green-900">{brandDetails.content.enriched}</p>
                        <p className="text-xs text-green-600">
                          {brandDetails.content.total > 0
                            ? `${Math.round((brandDetails.content.enriched / brandDetails.content.total) * 100)}%`
                            : '0%'}
                        </p>
                      </div>

                      {/* Search Queries */}
                      <div className="bg-purple-50 rounded-lg p-3">
                        <label className="text-xs text-purple-600 uppercase tracking-wider font-medium">Queries</label>
                        <p className="mt-1 text-2xl font-bold text-purple-900">{brandDetails.totalQueries}</p>
                        <p className="text-xs text-purple-600">search queries</p>
                      </div>

                      {/* Competitors */}
                      <div className="bg-orange-50 rounded-lg p-3">
                        <label className="text-xs text-orange-600 uppercase tracking-wider font-medium">Competitors</label>
                        <p className="mt-1 text-2xl font-bold text-orange-900">{brandDetails.competitors.total}</p>
                        <p className="text-xs text-orange-600">tracked</p>
                      </div>

                      {/* Head-to-Head */}
                      <div className="bg-red-50 rounded-lg p-3">
                        <label className="text-xs text-red-600 uppercase tracking-wider font-medium">Head-to-Head</label>
                        <p className="mt-1 text-2xl font-bold text-red-900">{brandDetails.competitors.headToHead}</p>
                        <p className="text-xs text-red-600">competitors</p>
                      </div>
                    </div>

                    {/* Content Status Breakdown */}
                    {(brandDetails.content.pending > 0 || brandDetails.content.failed > 0) && (
                      <div className="mt-3 flex items-center gap-4 text-xs">
                        {brandDetails.content.pending > 0 && (
                          <span className="flex items-center gap-1 text-yellow-600">
                            <Clock className="w-3 h-3" />
                            {brandDetails.content.pending} pending
                          </span>
                        )}
                        {brandDetails.content.failed > 0 && (
                          <span className="flex items-center gap-1 text-red-600">
                            <XCircle className="w-3 h-3" />
                            {brandDetails.content.failed} failed
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* User Brands with Processing Status */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      User Brands ({brandDetails.userBrands.length})
                    </h3>
                    {brandDetails.userBrands.length === 0 ? (
                      <p className="text-sm text-gray-500">No users associated with this brand</p>
                    ) : (
                      <div className="space-y-4">
                        {brandDetails.userBrands.map((ub) => {
                          const tier = ub.subscriptionProduct || 'FREE'
                          const hasInsights = tier === 'GROWTH' || tier === 'PRO'

                          // Helper for status badge
                          const StatusBadge = ({
                            label,
                            started,
                            complete,
                            completedAt,
                            disabled = false
                          }: {
                            label: string
                            started?: boolean
                            complete?: boolean
                            completedAt?: string | null
                            disabled?: boolean
                          }) => {
                            if (disabled) {
                              return (
                                <div className="flex items-center gap-2 text-gray-400 bg-gray-50 rounded px-2 py-1">
                                  <Lock className="w-3 h-3" />
                                  <span className="text-xs">{label}</span>
                                </div>
                              )
                            }
                            if (complete) {
                              return (
                                <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded px-2 py-1">
                                  <CheckCircle className="w-3 h-3" />
                                  <span className="text-xs">{label}</span>
                                  {completedAt && (
                                    <span className="text-xs text-green-600">
                                      {new Date(completedAt).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              )
                            }
                            if (started) {
                              return (
                                <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 rounded px-2 py-1">
                                  <Clock className="w-3 h-3 animate-pulse" />
                                  <span className="text-xs">{label}</span>
                                  <span className="text-xs text-yellow-600">Processing...</span>
                                </div>
                              )
                            }
                            return (
                              <div className="flex items-center gap-2 text-gray-500 bg-gray-50 rounded px-2 py-1">
                                <AlertCircle className="w-3 h-3" />
                                <span className="text-xs">{label}</span>
                                <span className="text-xs">Not started</span>
                              </div>
                            )
                          }

                          return (
                            <div
                              key={ub.id}
                              className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
                            >
                              {/* User Info Row */}
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {ub.user.firstName || ub.user.lastName
                                      ? `${ub.user.firstName || ''} ${ub.user.lastName || ''}`.trim()
                                      : 'No Name'}
                                  </p>
                                  <p className="text-sm text-gray-600">{ub.user.email}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    tier === 'PRO' ? 'bg-purple-100 text-purple-700' :
                                    tier === 'GROWTH' ? 'bg-blue-100 text-blue-700' :
                                    tier === 'STARTER' ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {tier}
                                  </span>
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    ub.subscriptionStatus === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                                    ub.subscriptionStatus === 'TRIAL' ? 'bg-yellow-100 text-yellow-700' :
                                    ub.subscriptionStatus === 'PAST_DUE' ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {ub.subscriptionStatus || 'NONE'}
                                  </span>
                                </div>
                              </div>

                              {/* Processing Status */}
                              <div className="border-t border-gray-100 pt-3">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                  Processing Status
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  <StatusBadge
                                    label="Search Analysis"
                                    started={ub.processingStatus?.searchAnalysisStarted}
                                    complete={ub.processingStatus?.searchAnalysisComplete}
                                    completedAt={ub.processingStatus?.searchAnalysisCompletedAt}
                                  />
                                  <StatusBadge
                                    label="Market Analysis"
                                    started={ub.processingStatus?.marketAnalysisStarted}
                                    complete={ub.processingStatus?.marketAnalysisComplete}
                                    completedAt={ub.processingStatus?.marketAnalysisCompletedAt}
                                  />
                                  <StatusBadge
                                    label="Content Enrichment"
                                    started={ub.processingStatus?.contentEnrichmentStarted}
                                    complete={ub.processingStatus?.contentEnrichmentComplete}
                                    completedAt={ub.processingStatus?.contentEnrichmentCompletedAt}
                                  />
                                </div>

                                {/* Insights Status - Only show for GROWTH+ */}
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 mt-3">
                                  Insights {!hasInsights && <span className="text-gray-400 normal-case">(GROWTH+ only)</span>}
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  <StatusBadge
                                    label="Search Insights"
                                    started={ub.processingStatus?.searchInsightsStarted}
                                    complete={ub.processingStatus?.searchInsightsComplete || !!ub.insights.searchInsights}
                                    completedAt={ub.processingStatus?.searchInsightsCompletedAt || ub.insights.searchInsights?.completedAt}
                                    disabled={!hasInsights}
                                  />
                                  <StatusBadge
                                    label="Market Insights"
                                    started={ub.processingStatus?.marketInsightsStarted}
                                    complete={ub.processingStatus?.marketInsightsComplete || !!ub.insights.marketInsights}
                                    completedAt={ub.processingStatus?.marketInsightsCompletedAt || ub.insights.marketInsights?.completedAt}
                                    disabled={!hasInsights}
                                  />
                                  <StatusBadge
                                    label="Brand Insights"
                                    started={ub.processingStatus?.brandInsightsStarted}
                                    complete={ub.processingStatus?.brandInsightsComplete || !!ub.insights.brandInsights}
                                    completedAt={ub.processingStatus?.brandInsightsCompletedAt || ub.insights.brandInsights?.completedAt}
                                    disabled={!hasInsights}
                                  />
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                  <p>Failed to load brand details</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
              <button
                onClick={handleCloseBrandModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
